/**
 * Centralized user type detection utility
 *
 * Provides consistent logic for detecting ZTR internal users vs external users
 * across the application to prevent security bypass opportunities.
 */

export const USER_TYPES = {
  ZTR_ORG_ID: 'ZTR',
  INTERNAL_ROLE: 'internal'
} as const;

export interface UserInfo {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  orgId?: string;
  isZTRUser?: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  orgId?: string;
  isZTRUser?: boolean;
  role?: 'admin' | 'user';
  userRole?: string;
}

/**
 * Centralized utility for detecting internal ZTR users
 */
export class UserTypeDetector {
  /**
   * Determines if a user is an internal team member based on authentication data
   *
   * Priority order:
   * 1. Explicit isZTRUser flag (from server authentication)
   * 2. Organization ID matching ZTR
   * 3. Role explicitly set to 'internal'
   *
   * @param userInfo - User information from various sources
   * @returns true if user is internal team member, false otherwise
   */
  static isInternalUser(userInfo?: UserInfo | AuthUser | null): boolean {
    if (!userInfo) {
      return false;
    }

    // Primary check: explicit ZTR user flag from authentication
    // If explicitly set (true or false), it takes priority
    if (userInfo.isZTRUser !== undefined) {
      return userInfo.isZTRUser;
    }

    // Secondary check: organization ID
    if (userInfo.orgId === USER_TYPES.ZTR_ORG_ID) {
      return true;
    }

    // Tertiary check: explicit internal role assignment
    if (userInfo.role === USER_TYPES.INTERNAL_ROLE) {
      return true;
    }

    return false;
  }

  /**
   * Validates that client-provided ZTR status matches server authentication
   * Used to prevent client-side manipulation of internal user privileges
   *
   * @param clientProvided - ZTR flag provided by client
   * @param serverAuth - Authenticated user data from server
   * @returns true if client claim is valid, false if potentially manipulated
   */
  static validateZTRClaim(clientProvided?: boolean, serverAuth?: AuthUser | null): boolean {
    // If no server auth available, we can't validate - allow the claim
    if (!serverAuth) {
      return true;
    }

    const actuallyInternal = this.isInternalUser(serverAuth);

    // If client claims internal but server says external, that's suspicious
    if (clientProvided === true && !actuallyInternal) {
      return false;
    }

    // If client claims external but server says internal, that's okay (conservative)
    // If both agree, that's okay
    // If client claim is undefined, that's okay
    return true;
  }

  /**
   * Gets the user type string for logging and analytics
   *
   * @param userInfo - User information
   * @returns 'internal' | 'external'
   */
  static getUserTypeString(userInfo?: UserInfo | AuthUser | null): 'internal' | 'external' {
    return this.isInternalUser(userInfo) ? 'internal' : 'external';
  }

  /**
   * Determines appropriate audience for content filtering
   *
   * @param userInfo - User information
   * @returns 'internal' for team members, 'customer' for external users
   */
  static getContentAudience(userInfo?: UserInfo | AuthUser | null): 'internal' | 'customer' {
    return this.isInternalUser(userInfo) ? 'internal' : 'customer';
  }
}